// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import {
  createCategory,
  deleteCategory,
  editCategory,
  getAllCategories,
  getCategoryList,
  toggleCategoryVisibility,
} from '@/modules/category/category.service'
import type {
  TCategoryDetail,
  TCreateCategoryRequest,
  TDeleteCategoryRequest,
  TEditCategoryRequest,
  TToggleCategoryVisibilityRequest,
} from '@/modules/category/category.types'

export const allCategoriesQueryOptions = () =>
  queryOptions({
    queryKey: ['allCategories'],
    queryFn: getAllCategories,
  })

export const categoryListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: ['categoryList', params],
    queryFn: () => getCategoryList(params),
  })

export function useCreateCategory({
  onSuccess,
  onError,
}: TMutation<TCategoryDetail, TResponseError, TCreateCategoryRequest>) {
  return useMutation<TCategoryDetail, TResponseError, TCreateCategoryRequest>({
    mutationFn: createCategory,
    onSuccess,
    onError,
  })
}

export function useEditCategory({
  onSuccess,
  onError,
}: TMutation<TCategoryDetail, TResponseError, TEditCategoryRequest>) {
  return useMutation<TCategoryDetail, TResponseError, TEditCategoryRequest>({
    mutationFn: editCategory,
    onSuccess,
    onError,
  })
}

export function useToggleCategoryVisibility({
  onSuccess,
  onError,
}: TMutation<
  TCategoryDetail,
  TResponseError,
  TToggleCategoryVisibilityRequest
>) {
  return useMutation<
    TCategoryDetail,
    TResponseError,
    TToggleCategoryVisibilityRequest
  >({
    mutationFn: toggleCategoryVisibility,
    onSuccess,
    onError,
  })
}

export function useDeleteCategory({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TDeleteCategoryRequest>) {
  return useMutation<void, TResponseError, TDeleteCategoryRequest>({
    mutationFn: deleteCategory,
    onSuccess,
    onError,
  })
}
